import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CatalogImportRequest } from '@/api/handlers/resources/catalogImports/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportRecord } from '@/api/models';
import { AccountQueryKeys } from '../accounts/accountQueryKeys';
import { AttributeQueryKeys } from '../attributes/attributeQueryKeys';
import { BrandQueryKeys } from '../brands/brandQueryKeys';
import { CategoryQueryKeys } from '../categories/categoryQueryKeys';
import { JournalQueryKeys } from '../journal/journalQueryKeys';
import { ProductQueryKeys } from '../products/productQueryKeys';
import { SupplierQueryKeys } from '../suppliers/supplierQueryKeys';
import { CatalogImportQueryKeys } from './catalogImportQueryKeys';

/**
 * Every cache an import or its undo can leave stale: the catalogue it creates
 * (or removes) rows in — products and stock, brands, categories and their
 * axes, suppliers, attribute types and their Pending values — and the ledger
 * its opening-stock entry (or that entry's reversal) posts to.
 */
export const CATALOG_IMPORT_TOUCHED_ROOTS = [
    ProductQueryKeys.all,
    BrandQueryKeys.all,
    CategoryQueryKeys.all,
    SupplierQueryKeys.all,
    AttributeQueryKeys.all,
    AccountQueryKeys.all,
    JournalQueryKeys.all,
] as const;

/**
 * Imports a catalog file (`POST catalog-imports`) and resolves to the record.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `ErrorResponse`. When rows still need attention it is a
 * `CatalogImportLinesInvalidError` whose `result` is the fresh per-row answer
 * (`isCatalogImportLinesInvalid`), for the table to show in place; every
 * other refusal keeps its code.
 *
 * On success it invalidates the branch's import history and every root in
 * {@link CATALOG_IMPORT_TOUCHED_ROOTS}. A refusal wrote nothing and invalidates nothing.
 */
export const useCatalogImportMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        CatalogImportRecord,
        ErrorResponse,
        CatalogImportRequest
    >({
        mutationFn: async (request) => {
            const result = await apiClient.catalogImports.import(request);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: (record) => {
            void queryClient.invalidateQueries({
                queryKey: CatalogImportQueryKeys.list(record.branchId),
            });
            // The every-branch history (the wizard's Done step) holds it too.
            void queryClient.invalidateQueries({
                queryKey: CatalogImportQueryKeys.list(null),
            });
            for (const queryKey of CATALOG_IMPORT_TOUCHED_ROOTS) {
                void queryClient.invalidateQueries({ queryKey });
            }
        },
    });
};
